import * as v from 'valibot';

// --- Learning Journey Context Types ---
export const LearningJourneyContextSchema = v.object({
  external_enrollment_id: v.string(),
  external_workflow_id: v.string(),
  external_version_id: v.string(),
  name: v.string(),
});

export interface LearningJourneyContext {
  external_enrollment_id: string;
  external_workflow_id: string;
  external_version_id: string;
  name: string;
}

export interface LearningJourneyCachedContextEntry {
  ljEnrollmentId: string;
  entryCreatedAt: number;
}

export const LEARNING_JOURNEY_CACHED_CONTEXT_KEY = 'lj-context';
export const LEARNING_JOURNEY_CACHED_CONTEXT_VALUE_KEY = 'ljEnrollmentId';

export const LEARNING_JOURNEY_ENROLLMENT_TYPE = 'journey-enrollment' as const;
export const COURSE_ENROLLMENT_TYPE = 'course-enrollment' as const;

export type EnrollmentType = typeof LEARNING_JOURNEY_ENROLLMENT_TYPE | typeof COURSE_ENROLLMENT_TYPE;

// --- Learning Journey State Types ---
export interface CourseNodeMetadata {
  course_id?: number;
  enrollment_id?: number;
}

export interface DelayNodeMetadata {
  execute_at?: string;
  enrollment_id?: number;
  previous_course_enrollment_id?: number;
}

export interface LearningJourneyState {
  user_id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  node_name: string;
  node_state: LearnerActivityNodeState;
  node_type: LearnerActivityNodeType;
  error: string;
  workflow_status: LearnerActivityStatus;
  error_details: CudErrorPlaceholders | null;
  course_node_metadata: CourseNodeMetadata;
  delay_node_metadata: DelayNodeMetadata;
  enrolled_at: string;
  completed_at: string | null;
  deleted_at: string | null;
}

export const CourseNodeMetadataSchema = v.object({
  course_id: v.number(),
  enrollment_id: v.number(),
});

export const DelayNodeMetadataSchema = v.object({
  execute_at: v.string(),
  enrollment_id: v.number(),
});

export const LearningJourneyStateSchema = v.object({
  user_id: v.number(),
  email: v.string(),
  username: v.string(),
  first_name: v.string(),
  last_name: v.string(),
  node_name: v.string(),
  node_state: v.string(),
  node_type: v.string(),
  error: v.string(),
  workflow_status: v.string(),
  error_details: v.union([v.object({ AttributeId: v.optional(v.string()) }), v.null_()]),
  course_node_metadata: v.object({}),
  delay_node_metadata: v.object({}),
  enrolled_at: v.string(),
  completed_at: v.union([v.string(), v.null_()]),
  deleted_at: v.union([v.string(), v.null_()]),
});

// --- Learner Activity Types ---
export interface LearnerActivityItem {
  user_id: number;
  completed_at?: string;
  email?: string;
  enrolled_at?: string;
  first_name?: string;
  last_name?: string;
  node_name?: string;
  node_state?: LearnerActivityNodeState;
  node_type?: LearnerActivityNodeType;
  username?: string;
  workflow_status?: LearnerActivityStatus;
  error?: WorkflowExecutionErrorCode;
  error_details?: CudErrorPlaceholders;
}

export type LearnerActivityNodeState =
  | LearnerActivityCourseNodeStates
  | LearnerActivityBranchNodeStates
  | LearnerActivityOtherNodeStates;

export enum LearnerActivityCourseNodeStates {
  ENROLLED_ON_COURSE = 'enrolled_on_course',
  ENROLLMENT_FAILED = 'enrollment_failed',
  NOT_STARTED = 'not_started',
}

export enum LearnerActivityBranchNodeStates {
  CONDITION_ISSUE = 'condition_issue',
  MISSING_DATA = 'missing_data',
  BLOCKED = 'blocked',
}

export enum LearnerActivityOtherNodeStates {
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  WAITING_FOR_COURSE_COMPLETION = 'waiting_for_course_completion',
}

export enum LearnerActivityNodeType {
  COURSE = 'course',
  DELAY = 'delay',
  BRANCH = 'branch',
  CERTIFICATE = 'certificate',
  START = 'start',
  END = 'end',
}

export enum LearnerActivityStatus {
  ENROLLED = 'enrolled',
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  BLOCKED = 'blocked',
  COMPLETED = 'completed',
  DONE = 'done',
}

export enum WorkflowExecutionErrorCode {
  LICENSE_CAPACITY_REACHED = 'license_capacity_reached_error',
  LICENSE_EXPIRED_ERROR = 'license_expired_error',
  TRAINING_CAPACITY_ERROR = 'training_capacity_error',
  MISSING_CUSTOM_USER_DATA_ATTRIBUTE_ERROR = 'missing_custom_user_data_attribute_error',
  MISSING_PREVIOUS_ENROLLMENT_ERROR = 'missing_previous_enrollment_error',
  NO_BRANCH_CONDITIONS_SATISFIED_ERROR = 'no_branch_condition_satisfied_error',
  NO_PREVIOUS_COURSE_CONDITION_SATISFIED_ERROR = 'no_previous_course_condition_satisfied',
}

export interface CudErrorPlaceholders {
  AttributeId?: string;
}
